import { Room } from '../types/chat';
import { isReactionMessage, isRedactionMessage, isReactionRedaction } from '../components/chat/utils';

// Базовый URL для статических ресурсов
const BASE_STATIC_URL = process.env.PUBLIC_URL || '';

// Доступные звуки уведомлений
export const AVAILABLE_SOUNDS = {
  default: `${BASE_STATIC_URL}/sounds/alert1.mp3`,
  bell: `${BASE_STATIC_URL}/sounds/alert2.mp3`,
  chime: `${BASE_STATIC_URL}/sounds/alert3.mp3`,
  ping: `${BASE_STATIC_URL}/sounds/alert4.mp3`,
  pop: `${BASE_STATIC_URL}/sounds/alert5.mp3`
};

// Тип для настроек уведомлений комнаты
export interface RoomNotificationSettings {
  enabled: boolean; // Включены ли звуковые уведомления
  soundUrl: string; // URL звука уведомления
}

// Класс для управления настройками уведомлений
class NotificationService {
  private static readonly SETTINGS_KEY = 'chat_notification_settings';
  private static readonly TITLE_PREFIX = 'Новое сообщение';
  private static readonly DEFAULT_TITLE = 'Matrix Chat';
  
  // Флаг для включения/отключения отладочных сообщений
  private static readonly DEBUG = false;
  
  private notificationSettings: Record<string, RoomNotificationSettings> = {};
  private originalTitle: string;
  private titleInterval: number | null = null;
  private audioElements: Record<string, HTMLAudioElement> = {};
  private hasUnreadMessages: boolean = false;
  private audioUnlocked: boolean = false;
  private pendingNotifications: Array<{ roomId: string, timestamp: number }> = [];
  private processingPendingNotifications: boolean = false;
  
  // Новое: отслеживание новых реакций для подсвечивания
  private newReactions: Set<string> = new Set(); // event_id реакций
  private reactionHighlightTimeout: Map<string, NodeJS.Timeout> = new Map();
  
  // Вспомогательный метод для логирования с проверкой флага DEBUG
  private log(message: string, ...args: any[]): void {
    if (NotificationService.DEBUG) {
      console.log(message, ...args);
    }
  }
  
  // Вспомогательный метод для логирования ошибок
  private logError(message: string, ...args: any[]): void {
    if (NotificationService.DEBUG) {
      console.error(message, ...args);
    }
  }
  
  // Вспомогательный метод для логирования предупреждений
  private logWarn(message: string, ...args: any[]): void {
    if (NotificationService.DEBUG) {
      console.warn(message, ...args);
    }
  }
  
  constructor() {
    this.originalTitle = document.title || this.getDefaultTitle();
    this.loadSettings();
    this.preloadAudioElements();
    this.setupAudioUnlockEvents();
  }
  
  // Настройка событий для разблокировки аудио
  private setupAudioUnlockEvents(): void {
    const unlockAudio = () => {
      if (this.audioUnlocked) return;
      
      //this.log('User interaction detected, attempting to unlock audio...');
      
      // Пробуем воспроизвести тихий звук, чтобы разблокировать аудио
      const silentAudio = new Audio(`${BASE_STATIC_URL}/sounds/alert1.mp3`);
      silentAudio.volume = 0.01; // Очень тихо
      
      // Попытка воспроизведения
      const playPromise = silentAudio.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this.log('Audio playback unlocked successfully!');
            this.audioUnlocked = true;
            silentAudio.pause();
            silentAudio.currentTime = 0;
            
            // Проигрываем отложенные уведомления
            this.processPendingNotifications();
          })
          .catch(error => {
            this.logWarn('Failed to unlock audio:', error);
          });
      }
      
      // Удаляем слушатели событий после успешной разблокировки
      if (this.audioUnlocked) {
        document.removeEventListener('click', unlockAudio);
        document.removeEventListener('keydown', unlockAudio);
        document.removeEventListener('touchstart', unlockAudio);
      }
    };
    
    // Добавляем слушатели событий
    document.addEventListener('click', unlockAudio);
    document.addEventListener('keydown', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);
  }
  
  // Обработка отложенных уведомлений
  private async processPendingNotifications(): Promise<void> {
    if (this.processingPendingNotifications || this.pendingNotifications.length === 0) return;
    
    this.processingPendingNotifications = true;
    
    try {
      this.log(`[DEBUG] Processing ${this.pendingNotifications.length} pending notifications`);
      
      // Перезагружаем настройки из localStorage для гарантии актуальности
      this.loadSettings();
      
      // Берем уведомления не старше 30 секунд
      const now = Date.now();
      const recentNotifications = this.pendingNotifications.filter(
        notification => now - notification.timestamp < 30000
      );
      
      this.log(`[DEBUG] Found ${recentNotifications.length} recent notifications (last 30 seconds)`);
      
      // Воспроизводим самое свежее уведомление, только если звук для комнаты включен
      if (recentNotifications.length > 0 && this.audioUnlocked) {
        const latestNotification = recentNotifications[recentNotifications.length - 1];
        this.log(`[DEBUG] Processing latest notification for room ${latestNotification.roomId}`);
        
        const settings = this.getRoomSettings(latestNotification.roomId);
        this.log(`[DEBUG] Room settings for pending notification: enabled=${settings.enabled}`);
        
        // Проверяем, включены ли звуковые уведомления для комнаты
        if (settings.enabled) {
          this.log(`[DEBUG] Playing pending notification for room ${latestNotification.roomId}`);
          await this.playNotificationSound(latestNotification.roomId);
        } else {
          this.log(`[DEBUG] Skipping notification sound for room ${latestNotification.roomId}: notifications disabled`);
        }
      } else {
        if (!this.audioUnlocked) {
          this.log(`[DEBUG] Audio not unlocked, keeping ${recentNotifications.length} notifications pending`);
        }
      }
      
      // Очищаем очередь
      this.pendingNotifications = [];
      this.log(`[DEBUG] Cleared pending notifications queue`);
    } catch (error) {
      this.logError('[DEBUG] Error processing pending notifications:', error);
    } finally {
      this.processingPendingNotifications = false;
    }
  }
  
  // Загрузка настроек из localStorage
  private loadSettings(): void {
    try {
      this.log('Loading notification settings from localStorage');
      const savedSettings = localStorage.getItem(NotificationService.SETTINGS_KEY);
      
      if (savedSettings) {
        try {
          const parsedSettings = JSON.parse(savedSettings);
          this.log('Loaded notification settings:', parsedSettings);
          
          // Проверяем, что загруженные настройки имеют правильный формат
          if (parsedSettings && typeof parsedSettings === 'object') {
            this.notificationSettings = parsedSettings;
          } else {
            this.logError('Invalid notification settings format:', parsedSettings);
            // Если формат неверный, сохраняем текущие настройки
            this.saveSettings();
          }
        } catch (parseError) {
          this.logError('Failed to parse notification settings:', parseError);
          // При ошибке парсинга сохраняем текущие настройки
          this.saveSettings();
        }
      } else {
        this.log('No saved notification settings found');
      }
    } catch (error) {
      this.logError('Failed to load notification settings:', error);
      this.notificationSettings = {};
    }
  }
  
  // Сохранение настроек в localStorage
  private saveSettings(): void {
    try {
      localStorage.setItem(
        NotificationService.SETTINGS_KEY,
        JSON.stringify(this.notificationSettings)
      );
    } catch (error) {
      this.logError('Failed to save notification settings:', error);
    }
  }
  
  // Предзагрузка аудио элементов для быстрого воспроизведения
  private preloadAudioElements(): void {
    this.log('Preloading audio elements for notification sounds');
    Object.entries(AVAILABLE_SOUNDS).forEach(([key, url]) => {
      try {
        const audio = new Audio();
        
        // Обработчики событий для отладки
        audio.addEventListener('canplaythrough', () => {
          this.log(`Audio ${key} loaded successfully:`, url);
        }, { once: true });
        
        audio.addEventListener('error', (e) => {
          this.logError(`Failed to load audio ${key} from ${url}:`, e);
        }, { once: true });
        
        // Настраиваем аудио и начинаем загрузку
        audio.preload = 'auto';
        audio.src = url;
        audio.load(); // Явно вызываем загрузку
        
        this.audioElements[key] = audio;
      } catch (err) {
        this.logError(`Error initializing audio ${key}:`, err);
      }
    });
  }
  
  // Получение дефолтного заголовка
  private getDefaultTitle(): string {
    return NotificationService.DEFAULT_TITLE;
  }
  
  // Проверка совпадения ID комнаты в разных форматах
  private isRoomIdMatch(roomId1: string, roomId2: string): boolean {
    if (!roomId1 || !roomId2) return false;
    
    // Используем нормализацию для обоих ID
    const normalized1 = this.normalizeRoomId(roomId1);
    const normalized2 = this.normalizeRoomId(roomId2);
    
    // Прямое совпадение после нормализации
    if (normalized1 === normalized2) {
      this.log(`[DEBUG] Room IDs match after normalization: ${roomId1} and ${roomId2} -> ${normalized1}`);
      return true;
    }
    
    // Дополнительные проверки для специфических случаев
    // Например, сравнение без учета доменной части для Matrix-идентификаторов
    const simplifiedMatch = normalized1.split(':')[0] === normalized2.split(':')[0];
    
    if (simplifiedMatch) {
      this.log(`[DEBUG] Room IDs match by simplified comparison: ${roomId1} and ${roomId2}`);
    }
    
    return simplifiedMatch;
  }
  
  // Получение настроек комнаты, с созданием дефолтных если их нет
  public getRoomSettings(roomId: string): RoomNotificationSettings {
    // Нормализуем ID комнаты
    const normalizedRoomId = this.normalizeRoomId(roomId);
    this.log(`[DEBUG] Getting settings for room ID: ${roomId}, normalized: ${normalizedRoomId}`);
    
    // Проверяем кэш настроек
    if (!this.notificationSettings[normalizedRoomId]) {
      // Проверяем, есть ли настройки для этой комнаты в другом формате
      const existingRoomId = Object.keys(this.notificationSettings).find(
        savedRoomId => this.isRoomIdMatch(savedRoomId, normalizedRoomId)
      );
      
      if (existingRoomId) {
        this.log(`[DEBUG] Found existing room settings with different ID format: ${existingRoomId} for ${normalizedRoomId}`);
        // Используем существующие настройки, но сохраняем их для нового формата
        this.notificationSettings[normalizedRoomId] = { ...this.notificationSettings[existingRoomId] };
      } else {
        this.log(`[DEBUG] Creating default sound settings for room ${normalizedRoomId}`);
        this.notificationSettings[normalizedRoomId] = {
          enabled: true,
          soundUrl: AVAILABLE_SOUNDS.default
        };
      }
      this.saveSettings();
    }
    
    const settings = this.notificationSettings[normalizedRoomId];
    this.log(`[DEBUG] Sound settings for room ${normalizedRoomId}: enabled=${settings.enabled}, soundUrl=${settings.soundUrl}`);
    return settings;
  }
  
  // Обновление настроек комнаты
  public updateRoomSettings(roomId: string, settings: Partial<RoomNotificationSettings>): void {
    const normalizedRoomId = this.normalizeRoomId(roomId);
    const currentSettings = this.getRoomSettings(normalizedRoomId);
    
    this.log(`[DEBUG] Updating room settings for ${roomId} (${normalizedRoomId}):`, settings);
    
    this.notificationSettings[normalizedRoomId] = {
      ...currentSettings,
      ...settings
    };
    
    this.saveSettings();
  }
  
  // Переключение звуковых уведомлений для комнаты
  public toggleSoundNotifications(roomId: string, enabled?: boolean): boolean {
    const normalizedRoomId = this.normalizeRoomId(roomId);
    const settings = this.getRoomSettings(normalizedRoomId);
    
    const currentState = settings.enabled;
    const newEnabled = enabled !== undefined ? enabled : !settings.enabled;
    
    this.log(`[DEBUG] Toggling sound notifications for room ${roomId} (${normalizedRoomId}): ${currentState} -> ${newEnabled}`);
    
    this.updateRoomSettings(normalizedRoomId, { enabled: newEnabled });
    return newEnabled;
  }
  
  // Изменение звука уведомлений для комнаты
  public changeSoundForRoom(roomId: string, soundUrl: string): void {
    const normalizedRoomId = this.normalizeRoomId(roomId);
    this.log(`[DEBUG] Changing sound for room ${roomId} (${normalizedRoomId}) to ${soundUrl}`);
    this.updateRoomSettings(normalizedRoomId, { soundUrl });
  }
  
  // Проигрывание звука уведомления для комнаты
  public async playNotificationSound(roomId: string): Promise<void> {
    try {
      this.log(`[DEBUG] Attempting to play notification sound for room ${roomId}`);
      const settings = this.getRoomSettings(roomId);
      this.log(`[DEBUG] Room settings: enabled=${settings.enabled}, soundUrl=${settings.soundUrl}`);
      
      if (!settings.enabled) {
        this.log(`[DEBUG] Sound notifications disabled for room ${roomId}, not playing sound`);
        return;
      }
      
      // Если аудио еще не разблокировано, добавляем уведомление в очередь
      if (!this.audioUnlocked) {
        this.log('[DEBUG] Audio not unlocked yet, adding to pending notifications queue');
        this.pendingNotifications.push({ roomId, timestamp: Date.now() });
        return;
      }
      
      // Находим ключ звука по URL
      const soundKey = Object.entries(AVAILABLE_SOUNDS)
        .find(([, url]) => url === settings.soundUrl)?.[0] || 'default';
      this.log(`[DEBUG] Using sound key: ${soundKey}, URL: ${settings.soundUrl}`);
      
      // Клонируем аудио элемент для предотвращения проблем с одновременным воспроизведением
      const audioTemplate = this.audioElements[soundKey];
      if (!audioTemplate) {
        throw new Error(`Audio template not found for key: ${soundKey}`);
      }
      
      // Создаем новый элемент для этого воспроизведения
      const audio = new Audio(audioTemplate.src);
      audio.volume = 0.5; // Устанавливаем громкость
      
      // Сброс и воспроизведение звука
      audio.currentTime = 0;
      this.log(`[DEBUG] Playing sound for room ${roomId} with src: ${audio.src}`);
      await audio.play();
      this.log(`[DEBUG] Sound played successfully for room ${roomId}`);
    } catch (err) {
      this.logError('[DEBUG] Error playing notification sound:', err);
      // Тихо обрабатываем ошибку, чтобы не прерывать работу приложения
    }
  }
  
  // Начать мигание заголовка страницы
  public startTitleNotification(): void {
    this.log('[DEBUG] Starting title notification blinking');
    this.hasUnreadMessages = true;
    
    // Очищаем предыдущий интервал, если он существует
    if (this.titleInterval) {
      this.log('[DEBUG] Clearing existing title interval before starting new one');
      window.clearInterval(this.titleInterval);
      this.titleInterval = null;
    }
    
    let showNotification = true;
    this.titleInterval = window.setInterval(() => {
      document.title = showNotification 
        ? `${NotificationService.TITLE_PREFIX} | ${this.originalTitle}`
        : this.originalTitle;
      showNotification = !showNotification;
    }, 1000);
    
    this.log('[DEBUG] Title notification blinking started');
  }
  
  // Остановить мигание заголовка
  public stopTitleNotification(): void {
    this.log('[DEBUG] Stopping title notification blinking');
    this.hasUnreadMessages = false;
    
    if (this.titleInterval) {
      window.clearInterval(this.titleInterval);
      this.titleInterval = null;
      document.title = this.originalTitle;
      this.log('[DEBUG] Title notification stopped, restored original title');
    } else {
      this.log('[DEBUG] No active title interval to stop');
    }
  }
  
  // Пометить комнату как прочитанную
  public markRoomAsRead(roomId: string, rooms: Record<string, Room>): Record<string, Room> {
    const updatedRooms = { ...rooms };
    if (updatedRooms[roomId]) {
      updatedRooms[roomId] = {
        ...updatedRooms[roomId],
        hasNewMessage: false
      };
    }
    
    // Проверяем, остались ли непрочитанные сообщения в других комнатах
    const hasOtherUnreadMessages = Object.values(updatedRooms).some(room => room.hasNewMessage);
    
    // Если нет других непрочитанных сообщений, останавливаем мигание заголовка
    if (!hasOtherUnreadMessages) {
      this.stopTitleNotification();
    }
    
    return updatedRooms;
  }
  
  // Проверка наличия непрочитанных сообщений в комнатах
  public checkUnreadMessages(rooms: Record<string, Room>): boolean {
    return Object.values(rooms).some(room => room.hasNewMessage);
  }
  
  // Проверка состояния разблокировки аудио
  public isAudioUnlocked(): boolean {
    return this.audioUnlocked;
  }
  
  // Ручная разблокировка аудио (может быть вызвана пользователем)
  public async unlockAudio(): Promise<boolean> {
    if (this.audioUnlocked) {
      this.log('[DEBUG] Audio already unlocked, no need to unlock again');
      return true;
    }
    
    this.log('[DEBUG] Attempting to unlock audio manually...');
    
    try {
      // Создаем и воспроизводим тихий звук
      const silentAudio = new Audio(`${BASE_STATIC_URL}/sounds/alert1.mp3`);
      silentAudio.volume = 0.01;
      
      // Пытаемся воспроизвести звук
      this.log('[DEBUG] Playing silent sound to unlock audio');
      await silentAudio.play();
      
      // Останавливаем звук после успешного воспроизведения
      silentAudio.pause();
      silentAudio.currentTime = 0;
      
      // Отмечаем, что аудио разблокировано
      this.audioUnlocked = true;
      this.log('[DEBUG] Audio unlocked successfully!');
      
      // Обрабатываем отложенные уведомления
      this.processPendingNotifications();
      
      return true;
    } catch (error) {
      this.logError('[DEBUG] Failed to manually unlock audio:', error);
      return false;
    }
  }
  
  // Попытка автоматически разблокировать аудио при загрузке страницы
  public async attemptSilentAutoUnlock(): Promise<boolean> {
    this.log('[DEBUG] Attempting silent auto-unlock of audio...');
    
    if (this.audioUnlocked) {
      this.log('[DEBUG] Audio already unlocked, no need for auto-unlock');
      return true;
    }
    
    try {
      // Создаем тихий звук
      const silentSound = new Audio('/sounds/alert1.mp3');
      silentSound.volume = 0;
      
      // Пытаемся воспроизвести
      await silentSound.play();
      
      // Если дошли сюда, значит ошибки не было - аудио разблокировано
      this.log('[DEBUG] Silent auto-unlock successful!');
      this.audioUnlocked = true;
      this.processPendingNotifications();
      return true;
    } catch (error) {
      // Звук был заблокирован политикой браузера
      this.log('[DEBUG] Silent auto-unlock failed:', error);
      return false;
    }
  }
  
  // Методы для работы с новыми реакциями
  
  // Добавить реакцию в список новых (для подсвечивания)
  public markReactionAsNew(reactionEventId: string): void {
    if (!reactionEventId) return;
    
    this.log(`[DEBUG] Marking reaction ${reactionEventId} as new for highlighting`);
    this.newReactions.add(reactionEventId);
    
    // Удаляем подсвечивание через 3 секунды
    const timeoutId = setTimeout(() => {
      this.log(`[DEBUG] Removing highlight for reaction ${reactionEventId}`);
      this.newReactions.delete(reactionEventId);
      this.reactionHighlightTimeout.delete(reactionEventId);
    }, 3000);
    
    // Очищаем предыдущий таймер если был
    const existingTimeout = this.reactionHighlightTimeout.get(reactionEventId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    this.reactionHighlightTimeout.set(reactionEventId, timeoutId);
  }
  
  // Проверить, является ли реакция новой (для подсвечивания)
  public isReactionNew(reactionEventId: string): boolean {
    return this.newReactions.has(reactionEventId);
  }
  
  // Очистить все новые реакции (например, при смене комнаты)
  public clearNewReactions(): void {
    this.log('[DEBUG] Clearing all new reaction highlights');
    
    // Очищаем все таймеры
    this.reactionHighlightTimeout.forEach(timeout => clearTimeout(timeout));
    this.reactionHighlightTimeout.clear();
    
    // Очищаем набор новых реакций
    this.newReactions.clear();
  }
  
  // Метод для тестирования уведомлений реакций (только для отладки)
  public testReactionNotification(roomId: string): void {
    if (!NotificationService.DEBUG) return;
    
    this.log('[DEBUG] Testing reaction notification for room:', roomId);
    
    // Создаем тестовую реакцию
    const testReactionId = `test_reaction_${Date.now()}`;
    this.markReactionAsNew(testReactionId);
    
    // Проигрываем звук уведомления
    this.playNotificationSound(roomId).catch(error => {
      this.logWarn('[DEBUG] Failed to play test notification:', error);
    });
    
    // Мигаем заголовком
    this.startTitleNotification();
    
    console.log('🎭 Тестовое уведомление реакции отправлено!');
  }
  
  // Обновленный метод handleNewMessage с поддержкой реакций
  public handleNewMessage(
    roomId: string, 
    isFromMe: boolean, 
    rooms: Record<string, Room>,
    activeRoomId: string | null,
    message?: any, // Полный объект сообщения для анализа
    allMessages?: any[]
  ): Record<string, Room> {
    const messageType = message?.type;
    this.log(`[DEBUG] handleNewMessage: roomId=${roomId}, isFromMe=${isFromMe}, activeRoomId=${activeRoomId}, messageType=${messageType}`);
    
    // Не уведомлять о собственных сообщениях
    if (isFromMe) {
      this.log('[DEBUG] Message is from current user, skipping notification');
      return rooms;
    }
    
    // Если это новая реакция, помечаем её для подсвечивания
    if (messageType === 'm.reaction' && message?.event_id) {
      this.markReactionAsNew(message.event_id);
    }
    
    // Нормализуем ID комнаты для более надежного сравнения
    const normalizedRoomId = this.normalizeRoomId(roomId);
    const normalizedActiveRoomId = activeRoomId ? this.normalizeRoomId(activeRoomId) : null;
    this.log(`[DEBUG] Normalized roomId: ${normalizedRoomId}, activeRoomId: ${normalizedActiveRoomId}`);
    
    // Проверяем, является ли комната активной
    const isActiveRoom = normalizedActiveRoomId === normalizedRoomId;
    this.log(`[DEBUG] Is active room check: ${isActiveRoom}`);
    
    // Проверяем тип сообщения для специальной обработки
    if (messageType === 'm.room.redaction' && allMessages && message) {
      // Проверяем, является ли это удалением реакции
      if (isReactionRedaction(message, allMessages)) {
        this.log('[DEBUG] This is a reaction redaction, skipping notification');
        return rooms; // Не показываем уведомления для удаления реакций
      }
    }
    
    // Обновить состояние комнаты, добавив индикатор нового сообщения
    const updatedRooms = { ...rooms };
    if (updatedRooms[roomId]) {
      // Всегда помечаем сообщение как непрочитанное, даже в активной комнате
      // Оно будет помечено как прочитанное только при явном взаимодействии пользователя
      updatedRooms[roomId] = {
        ...updatedRooms[roomId],
        hasNewMessage: true
      };
      
      // Перезагрузим настройки из localStorage, чтобы быть уверенными в их актуальности
      this.loadSettings();
      
      // Проверяем настройки звука для этой комнаты
      const settings = this.getRoomSettings(normalizedRoomId);
      this.log(`[DEBUG] Room ${normalizedRoomId} settings: enabled=${settings.enabled}, soundUrl=${settings.soundUrl}`);
      
      // Проигрывать звук если для комнаты включены звуковые уведомления
      // Для реакций также воспроизводим звук (если не отключен)
      const shouldPlaySound = settings.enabled && (
        messageType === 'm.reaction' || // Новая реакция
        messageType === 'm.room.message' || // Обычное сообщение  
        !messageType // Неизвестный тип - воспроизводим на всякий случай
      );
      
      if (shouldPlaySound) {
        this.log(`[DEBUG] Sound enabled for room ${normalizedRoomId}, playing notification for message type ${messageType}`);
        this.playNotificationSound(normalizedRoomId).catch(error => {
          this.logWarn('[DEBUG] Failed to play notification sound:', error);
        });
      } else {
        this.log(`[DEBUG] Sound disabled or message type ${messageType} doesn't require sound for room ${normalizedRoomId}`);
      }
      
      // Всегда запускаем мигание заголовка при новых сообщениях, независимо от того активна комната или нет
      // Но только для значимых типов сообщений
      const shouldBlinkTitle = 
        messageType === 'm.reaction' || // Новая реакция
        messageType === 'm.room.message' || // Обычное сообщение
        !messageType; // Неизвестный тип
      
      if (shouldBlinkTitle) {
        this.log(`[DEBUG] Starting title notification for new message type ${messageType} in room ${normalizedRoomId} (active: ${isActiveRoom})`);
        this.startTitleNotification();
      } else {
        this.log(`[DEBUG] Skipping title notification for message type ${messageType} in room ${normalizedRoomId}`);
      }
    } else {
      this.log(`[DEBUG] Room ${roomId} not found in rooms list, can't handle message`);
    }
    
    return updatedRooms;
  }
  
  // Нормализация ID комнаты для более надежного сравнения
  private normalizeRoomId(roomId: string): string {
    if (!roomId) return '';
    
    // Удаляем префиксы типа ! или # и пробелы с начала и конца
    return roomId.replace(/^[!#]/, '').trim();
  }
}

export const notificationService = new NotificationService();

export default notificationService; 